<template>
  <v-app class="app-background-color">
    <template v-if="!computedLeftMenu">
      <LeftMenu
        v-if="!isSmAndDown && computedLinks.length"
        :links="computedLinks"
      />
    </template>

    <Navbar
      :links="computedLinks"
      :isSmAndDown="isSmAndDown"
      :isXs="isXs"
      @openHelpDialog="helpDialog = true"
    />

    <v-main
      class="main ml-9 mr-4"
      :class="{ mobile: isSmAndDown }"
    >
      <router-view :isSmAndDown="isSmAndDown" />
      <BaseDialog
        title="Помощь"
        :dialog="helpDialog"
        :persistent="false"
        :is-show-mode="true"
        @close="helpDialog = false"
      >
        <div v-if="userId">
          <v-row class="ml-0 mb-5">
            <v-col
              cols="12"
              class="pa-0 pt-5"
            >
              <ul class="links">
                <li
                  v-for="instruction in instructions"
                  :key="instruction.id"
                >
                  <a
                    class="link"
                    :href="instruction.link"
                    target="_blank"
                  >
                    {{ instruction.name }}
                  </a>
                </li>
              </ul>
            </v-col>
          </v-row>

          При возникновении вопросов по работе сервиса отправляйте заявку на почту
          <a
            class="link"
            href="mailto:Help_NIR@szgmu.ru"
            >Help_NIR@szgmu.ru</a
          >
        </div>
        <div v-else>
          <b>Для регистрации в Сервисе</b>
          необходимо отправить заявку на почту
          <a href="mailto:Help_NIR@szgmu.ru">Help_NIR@szgmu.ru</a>, указав в ней:

          <ol class="mt-1 mb-1">
            <li>ФИО сотрудника</li>
            <li>Наименование подразделения</li>
            <li>Должность</li>
            <li>Вид должностного исполнения (основной, внут.совместительство, внеш.совместительство)</li>
            <li>Ученая степень</li>
            <li>Размер ставки</li>
            <li>Электронная почта</li>
          </ol>

          <p>
            После внесения в базу указанных данных вам придет письмо с подтверждением возможности регистрации. После
            этого <b>в Сервисе</b> на странице входа необходимо нажать <b>«Регистрация»</b>, далее указать электронную
            почту. На указанную почту придет ссылка для подтверждения регистрации, по которой необходимо перейти и
            указать пароль, который вы будете использовать для входа <b>в Сервис</b>.
          </p>

          <p>
            <b>Для восстановления пароля в Сервисе</b>
            на странице входа необходимо нажать <b>«Забыли пароль?»</b>, далее указать электронную почту, по которой вы
            регистрировались. На указанную почту придет ссылка для подтверждения восстановления пароля, по которой
            необходимо перейти и указать пароль, который вы будете использовать для входа <b>в Сервис</b>.
          </p>

          <p>
            <b>Для работы в Сервисе рекомендуется использовать браузер Google Chrome.</b>
          </p>
        </div>
      </BaseDialog>
    </v-main>

    <v-footer
      padless
      class="app-background-color footer"
    >
      <v-col
        class="caption accent--text px-4"
        cols="12"
      >
        © {{ new Date().getFullYear() }}, Сервис учета информации о научных работах
      </v-col>
    </v-footer>
  </v-app>
</template>

<script>
import { mapGetters } from 'vuex'
import Navbar from '@/components/ui/Navbar'
import LeftMenu from '@/components/ui/LeftMenu'
import BaseDialog from '@/components/ui/BaseDialog.vue'
import { links } from '@/router/links'

export default {
  name: 'App',

  components: {
    Navbar,
    LeftMenu,
    BaseDialog
  },

  data: () => ({
    isSmAndDown: false,
    isXs: false,
    helpDialog: false,
    instructions: [
      {
        id: 1,
        name: 'Инструкция аудитора',
        link: '/instructions/Инструкция аудитора.docx'
      },
      {
        id: 2,
        name: 'Инструкция модератора',
        link: '/instructions/Инструкция модератора.docx'
      },
      {
        id: 3,
        name: 'Инструкция ответственного кафедры',
        link: '/instructions/Инструкция ответственного кафедры.docx'
      },
      {
        id: 4,
        name: 'Инструкция сотрудника',
        link: '/instructions/Инструкция сотрудника.docx'
      },
      {
        id: 5,
        name: 'Сроки кварталов',
        link: '/instructions/Сроки кварталов.pdf'
      },
      {
        id: 6,
        name: 'Часто задаваемые вопросы',
        link: '/instructions/Часто задаваемые вопросы.pdf'
      },
      {
        id: 7,
        name: 'Категорирование журналов ВАК',
        link: '/instructions/Категорирование журналов ВАК.PDF'
      },
      {
        id: 8,
        name: 'Соглашение авторов',
        link: '/instructions/Соглашение о долевом участии шаблон.doc'
      }
    ]
  }),

  async mounted() {
    this.onResize()
    window.addEventListener('resize', this.onResize)
    await this.findActualizationJournals()
  },

  destroyed() {
    window.removeEventListener('resize', this.onResize)
  },

  computed: {
    ...mapGetters('user', ['userId', 'allUserRoles', 'isAdmin']),

    computedLinks() {
      if (!this.userId) return []

      return links.filter(link => link.allowRoles.some(role => this.allUserRoles.includes(role)))
    },

    computedLeftMenu() {
      return ['userParent', 'roleParent', 'userGroupParent', 'login'].includes(this.$route.name)
    }
  },

  methods: {
    onResize() {
      this.isSmAndDown = window.innerWidth < 960
      this.isXs = window.innerWidth < 620
    },
    async findActualizationJournals() {
      try {
        const { journalEditorialBoardApi } = await import('@/api')
        const actualizeJournals = await journalEditorialBoardApi.findActualizationForCurrentUser()
        if (actualizeJournals.length)
          this.$toast.warning(
            `У вас есть работа в журнале, которую нужно актуализировать. Перейдите в раздел "Участие в работе редакционных коллегий журналов", актуализируйте статус участия и отправьте на проверку. `,
            {
              timeout: 10000,
              onClick: () => {
                this.$router.push('/journal-editorail-boards/journals')
              }
            }
          )
      } catch (e) {
        console.error(e)
      }
    }
  }
}
</script>

<style>
@import '~@/assets/styles/index.css';

.custom-tabs-arrows .v-slide-group__prev {
  min-width: 28px !important;
  justify-content: left !important;
  flex: 0 1 32px !important;
}

.custom-tabs-arrows .v-slide-group__next {
  min-width: 28px !important;
  justify-content: left !important;
  flex: 0 1 32px !important;
}

.custom-tabs-arrows .v-slide-group__next i {
  margin-left: 6px;
}
</style>
